import { useState, useCallback, useRef } from "react";
import {
  SessionState,
  Category,
  SCHOOL_CATEGORIES,
  isCellFilled,
  filledCount,
} from "../types/game";

export type GameNotifType =
  | "school_flunker"      // school finished with negative total
  | "school_graduated"    // school finished, no bonus
  | "school_honors"       // school finished with +50 or +100 bonus
  | "small_but_mine"      // four_of_kind or poker with low dice values
  | "main_game_started"   // every player passed round 3 — combo fields unlocked
  | "poker_rolled"        // poker scored with big dice — the jackpot moment
  | "take_the_lead"       // a new leader emerged mid-game (realtime scores only)
  | "last_round"          // every player is one cell away from finishing
  | "crossed_out"         // poker or big straight crossed out — tough luck
  | "achievement_unlocked";

export interface GameNotif {
  id: number;
  type: GameNotifType;
  playerName: string;
  playerAvatar: string | null;
  achievementName?: string;
  achievementIcon?: string;
}

let notifCounter = 0;

/**
 * Compute school bonus from scorecard values.
 * Mirrors backend logic: all 6 filled & sum < 0 → -50,
 * all zero → +50, all positive → +100, mixed ≥ 0 → 0.
 */
function computeSchoolBonus(scorecard: Record<string, number | null>): {
  sum: number;
  bonus: number;
  total: number;
} {
  const values = SCHOOL_CATEGORIES.map((c) => scorecard[c as Category]);
  const allFilled = values.every((v) => v !== null);
  if (!allFilled) return { sum: 0, bonus: 0, total: 0 };

  const filled = values as number[];
  const sum = filled.reduce((a, b) => a + b, 0);
  const allPositive = filled.every((v) => v > 0);
  const allZero = filled.every((v) => v === 0);

  let bonus = 0;
  if (sum < 0) bonus = -50;
  else if (allZero) bonus = 50;
  else if (allPositive) bonus = 100;

  return { sum, bonus, total: sum + bonus };
}

/**
 * Detects game events from state changes and fires notifications.
 * Returns the current notification queue and a dismiss handler.
 */
// True when every player has filled at least 3 cells — the combo ("main game")
// fields are unlocked after the 3rd round for everyone.
// filledCount works on redacted final_only payloads too.
function combosUnlocked(state: SessionState): boolean {
  if (state.players_with_scorecards.length === 0) return false;
  return state.players_with_scorecards.every((p) => filledCount(p) >= 3);
}

// Player id of the current sole leader by grand_total, or null when tied/empty.
function soleLeader(state: SessionState): { id: string; name: string; avatar: string | null } | null {
  const ranked = [...state.players_with_scorecards].sort(
    (a, b) => b.totals.grand_total - a.totals.grand_total
  );
  if (ranked.length < 2) return null;
  if (ranked[0].totals.grand_total <= 0) return null;
  if (ranked[0].totals.grand_total === ranked[1].totals.grand_total) return null;
  return {
    id: ranked[0].player.id,
    name: ranked[0].player.display_name,
    avatar: ranked[0].player.avatar,
  };
}

export function useGameNotifications() {
  const [notifications, setNotifications] = useState<GameNotif[]>([]);
  const prevSchoolComplete = useRef<Record<string, boolean>>({});
  const mainGameAnnounced = useRef(false);
  const lastRoundAnnounced = useRef(false);
  const prevLeaderId = useRef<string | null>(null);

  const checkEvents = useCallback(
    (prev: SessionState | null, next: SessionState) => {
      if (!prev) {
        // On first load, record current school completion state so we don't
        // fire notifications for already-completed school sections
        for (const p of next.players_with_scorecards) {
          const allFilled = SCHOOL_CATEGORIES.every((c) => isCellFilled(p, c));
          prevSchoolComplete.current[p.player.id] = allFilled;
        }
        mainGameAnnounced.current = combosUnlocked(next);
        lastRoundAnnounced.current =
          next.players_with_scorecards.length > 0 &&
          next.players_with_scorecards.every((p) => filledCount(p) >= 14);
        prevLeaderId.current = soleLeader(next)?.id ?? null;
        return;
      }

      // ── Round 3 done for everyone → main game fields unlocked ──
      if (!mainGameAnnounced.current && combosUnlocked(next)) {
        mainGameAnnounced.current = true;
        pushNotif("main_game_started", "", null);
      }

      // ── Everyone is one cell away from the finish line ──
      const inLastRound =
        next.players_with_scorecards.length > 0 &&
        next.players_with_scorecards.every(
          (p) => filledCount(p) >= 14 && filledCount(p) < 15
        );
      if (!lastRoundAnnounced.current && inLastRound) {
        lastRoundAnnounced.current = true;
        pushNotif("last_round", "", null);
      }

      // ── Lead change (only when scores are visible and school is done) ──
      const scoresVisible = next.session.score_visibility !== "final_only";
      if (
        scoresVisible &&
        next.session.status === "active" &&
        next.players_with_scorecards.every((p) => filledCount(p) >= 6)
      ) {
        const leader = soleLeader(next);
        if (leader && prevLeaderId.current && leader.id !== prevLeaderId.current) {
          pushNotif("take_the_lead", leader.name, leader.avatar);
        }
        if (leader) prevLeaderId.current = leader.id;
      }

      // Under "final only" visibility, don't leak school outcomes mid-game —
      // fire a neutral notification instead of flunker/honors.
      const hideOutcomes =
        next.session.score_visibility === "final_only" &&
        next.session.status !== "finished";

      for (const p of next.players_with_scorecards) {
        const allFilled = SCHOOL_CATEGORIES.every((c) => isCellFilled(p, c));
        const wasComplete = prevSchoolComplete.current[p.player.id] ?? false;

        // ── School just completed ────────────────────────────
        if (allFilled && !wasComplete) {
          prevSchoolComplete.current[p.player.id] = true;

          // Compute totals from scorecard values (not p.totals)
          // because optimistic updates change scorecard but NOT totals.
          const { total, bonus } = computeSchoolBonus(p.scorecard as unknown as Record<string, number | null>);

          if (hideOutcomes) {
            pushNotif("school_graduated", p.player.display_name, p.player.avatar);
          } else if (total < 0) {
            pushNotif("school_flunker", p.player.display_name, p.player.avatar);
          } else if (bonus > 0) {
            pushNotif("school_honors", p.player.display_name, p.player.avatar);
          } else {
            pushNotif("school_graduated", p.player.display_name, p.player.avatar);
          }
        }

        // ── Four of a Kind / Poker with small dice ───────────
        const prevP = prev.players_with_scorecards.find(
          (pp) => pp.player.id === p.player.id
        );
        if (prevP) {
          for (const cat of ["four_of_kind", "poker"] as const) {
            const prevVal = prevP.scorecard[cat];
            const nextVal = p.scorecard[cat];
            // Just filled in (was null, now has a value > 0)
            if (prevVal === null && nextVal !== null && nextVal > 0) {
              // Check if the dice sum (without bonus) is "small"
              // four_of_kind: bonus is +20, poker: bonus is +50
              const bonus = cat === "four_of_kind" ? 20 : 50;
              const diceSum = nextVal - bonus;
              // "Small" means the raw dice sum ≤ 8 (four 1s=4, four 2s=8, five 1s=5, five 2s=10)
              if (diceSum <= 8) {
                pushNotif("small_but_mine", p.player.display_name, p.player.avatar);
              } else if (cat === "poker") {
                // A real poker with big dice — jackpot fanfare
                pushNotif("poker_rolled", p.player.display_name, p.player.avatar);
              }
            }
          }

          // Crossing out the big combos hurts — acknowledge it
          for (const cat of ["poker", "straight2"] as const) {
            if (prevP.scorecard[cat] === null && p.scorecard[cat] === 0) {
              pushNotif("crossed_out", p.player.display_name, p.player.avatar);
            }
          }
        }
      }
    },
    // pushNotif is a stable useCallback with no deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const pushNotif = useCallback(
    (type: GameNotifType, playerName: string, playerAvatar: string | null) => {
      const id = ++notifCounter;
      const notif: GameNotif = { id, type, playerName, playerAvatar };
      setNotifications((prev) => [...prev, notif]);

      // Auto-dismiss after 3.5s
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, 3500);
    },
    []
  );

  const pushAchievement = useCallback(
    (playerName: string, playerAvatar: string | null, achievementName: string, achievementIcon: string) => {
      const id = ++notifCounter;
      const notif: GameNotif = { 
        id, 
        type: "achievement_unlocked", 
        playerName, 
        playerAvatar,
        achievementName,
        achievementIcon
      };
      setNotifications((prev) => [...prev, notif]);

      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, 5000); // Achievements stay a bit longer
    },
    []
  );

  const dismiss = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return { notifications, checkEvents, pushAchievement, dismiss };
}
